"use client";

import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { useAuth } from '@/context/auth-context';

type VideoMode = 'mini' | 'large';

export interface PlaylistItem {
  fileId: string;
  title: string;
}

export interface SubtitleTrack {
  lang: string;
  label: string;
  url: string;
}

interface PlayerContextType {
  streamUrl: string | null;
  mediaTitle: string | null;
  currentFileId: string | null;
  playlist: PlaylistItem[];
  playlistIndex: number;
  videoMode: VideoMode;
  totalDuration: number | null;
  subtitleTracks: SubtitleTrack[];
  startPosition: number | null;
  hasNext: boolean;
  hasPrev: boolean;

  playMedia: (id: string, title: string) => Promise<void>;
  playFile: (fileId: string, title: string, playlist?: PlaylistItem[], index?: number) => Promise<void>;
  playNext: () => void;
  playPrev: () => void;
  closePlayer: () => void;
  setVideoMode: (mode: VideoMode) => void;
  reportProgress: (position: number, duration: number) => void;
}

const PlayerContext = createContext<PlayerContextType | undefined>(undefined);

export const PlayerProvider = ({ children }: { children: React.ReactNode }) => {
  const { token } = useAuth();

  const [streamUrl, setStreamUrl]           = useState<string | null>(null);
  const [mediaTitle, setMediaTitle]         = useState<string | null>(null);
  const [currentFileId, setCurrentFileId]   = useState<string | null>(null);
  const [playlist, setPlaylist]             = useState<PlaylistItem[]>([]);
  const [playlistIndex, setPlaylistIndex]   = useState(0);
  const [videoMode, setVideoMode]           = useState<VideoMode>('mini');
  const [totalDuration, setTotalDuration]   = useState<number | null>(null);
  const [subtitleTracks, setSubtitleTracks] = useState<SubtitleTrack[]>([]);
  const [startPosition, setStartPosition]   = useState<number | null>(null);

  const lastSavedRef = useRef<number>(0);

  const hasNext = playlist.length > 0 && playlistIndex < playlist.length - 1;
  const hasPrev = playlistIndex > 0;

  // Core play function – resolves stream URL, subtitles, and saved position for a fileId
  const loadFile = useCallback(async (fileId: string, title: string) => {
    const streamRes = await fetch(`/api/media/file/${fileId}/stream`);
    if (!streamRes.ok) throw new Error(`Stream error ${streamRes.status}`);
    const streamData = await streamRes.json();

    // Subtitle tracks (non-blocking)
    let tracks: SubtitleTrack[] = [];
    try {
      const tracksRes = await fetch(`/api/media/file/${fileId}/tracks`);
      if (tracksRes.ok) tracks = await tracksRes.json();
    } catch { /* ignore */ }

    // Saved watch position (non-blocking)
    let savedPos: number | null = null;
    if (token) {
      try {
        const histRes = await fetch(`/api/media/history/${fileId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (histRes.ok) {
          const hist = await histRes.json();
          if (hist?.position > 30 && hist?.duration && (hist.position / hist.duration) < 0.9) {
            savedPos = hist.position;
          }
        }
      } catch { /* ignore */ }
    }

    setStreamUrl(streamData.streamUrl);
    setTotalDuration(streamData.duration || null);
    setMediaTitle(title);
    setCurrentFileId(fileId);
    setSubtitleTracks(tracks);
    setStartPosition(savedPos);
    setVideoMode('large');
    lastSavedRef.current = 0;
  }, [token]);

  // Play a specific file, with optional episode playlist
  const playFile = useCallback(async (
    fileId: string,
    title: string,
    pl?: PlaylistItem[],
    index?: number
  ) => {
    try {
      setPlaylist(pl ?? [{ fileId, title }]);
      setPlaylistIndex(index ?? 0);
      await loadFile(fileId, title);
    } catch (err) {
      console.error('playFile error:', err);
      alert('Erreur lors du lancement de la vidéo.');
    }
  }, [loadFile]);

  // Play a media by mediaId or 'file/xxx' — always resolves to loadFile for full track support
  const playMedia = useCallback(async (id: string, title: string) => {
    try {
      let fileId: string;
      if (id.startsWith('file/')) {
        fileId = id.slice(5);
      } else {
        // Resolve mediaId → fileId via stream endpoint
        const res = await fetch(`/api/media/${id}/stream`);
        if (!res.ok) throw new Error(`Stream error ${res.status}`);
        const data = await res.json();
        fileId = data.fileId;
        if (!fileId) throw new Error('No fileId in stream response');
      }
      setPlaylist([{ fileId, title }]);
      setPlaylistIndex(0);
      await loadFile(fileId, title);
    } catch (err) {
      console.error('playMedia error:', err);
      alert('Erreur lors du lancement de la vidéo.');
    }
  }, [loadFile]);

  const playNext = useCallback(() => {
    if (!hasNext) return;
    const nextIdx = playlistIndex + 1;
    const next = playlist[nextIdx];
    setPlaylistIndex(nextIdx);
    loadFile(next.fileId, next.title).catch(console.error);
  }, [hasNext, playlistIndex, playlist, loadFile]);

  const playPrev = useCallback(() => {
    if (!hasPrev) return;
    const prevIdx = playlistIndex - 1;
    const prev = playlist[prevIdx];
    setPlaylistIndex(prevIdx);
    loadFile(prev.fileId, prev.title).catch(console.error);
  }, [hasPrev, playlistIndex, playlist, loadFile]);

  const closePlayer = useCallback(() => {
    setStreamUrl(null);
    setMediaTitle(null);
    setCurrentFileId(null);
    setTotalDuration(null);
    setSubtitleTracks([]);
    setStartPosition(null);
    setPlaylist([]);
    setPlaylistIndex(0);
  }, []);

  // Called by VideoPlayer every ~15s while playing, and on pause/close
  const reportProgress = useCallback((position: number, duration: number) => {
    if (!currentFileId || !token) return;
    if (Math.abs(position - lastSavedRef.current) < 5) return; // debounce
    lastSavedRef.current = position;

    fetch('/api/media/history', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({ fileId: currentFileId, position, duration })
    }).catch(() => { /* fire-and-forget */ });
  }, [currentFileId, token]);

  return (
    <PlayerContext.Provider value={{
      streamUrl, mediaTitle, currentFileId,
      playlist, playlistIndex, videoMode, totalDuration,
      subtitleTracks, startPosition, hasNext, hasPrev,
      playMedia, playFile, playNext, playPrev,
      closePlayer, setVideoMode, reportProgress
    }}>
      {children}
    </PlayerContext.Provider>
  );
};

export const usePlayer = () => {
  const ctx = useContext(PlayerContext);
  if (!ctx) throw new Error('usePlayer must be used within a PlayerProvider');
  return ctx;
};
